import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronUp } from "lucide-react";
import LogoImage from "./LogoImage";
import { ProductBadges } from "./Badges";
import { useUpvotes, getLocalCount, bumpLocalCount } from "../lib/upvotes";

export default function ProductCard({ product, compact = false }) {
    const upv = useUpvotes();
    const [count, setCount] = useState(
        () => product.upvotes + getLocalCount(product.slug),
    );
    const hasVoted = upv.has(product.slug);
    const [expanded, setExpanded] = useState(false);

    const handleVote = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (hasVoted) return;
        upv.add(product.slug);
        const localPrev = getLocalCount(product.slug);
        const next = bumpLocalCount(product.slug, localPrev);
        setCount(product.upvotes + next);
    };

    const TRUNCATE = 240;
    const longDescription = (product.description || "").length > TRUNCATE;
    const description =
        expanded || compact || !longDescription
            ? product.description
            : product.description.slice(0, TRUNCATE).trimEnd() + "…";

    return (
        <article
            className="bg-white border border-line rounded-2xl p-5 sm:p-6 card-lift"
            data-testid={`product-card-${product.slug}`}
        >
            <div className="flex flex-col sm:flex-row gap-5">
                <Link
                    to={`/product/${product.slug}`}
                    className="shrink-0"
                    aria-label={product.name}
                >
                    <LogoImage
                        src={product.logo_url}
                        domain={product.domain}
                        name={product.name}
                        size={64}
                    />
                </Link>

                <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-start gap-3 justify-between">
                        <div className="min-w-0">
                            <Link
                                to={`/product/${product.slug}`}
                                className="block hover:text-saffron transition-colors"
                                data-testid={`product-link-${product.slug}`}
                            >
                                <h3 className="font-heading font-bold text-lg text-ink truncate">
                                    {product.name}
                                </h3>
                            </Link>
                            {product.why_indian && (
                                <p className="mt-0.5 text-xs text-saffron-700 font-medium">
                                    🇮🇳 {product.why_indian}
                                </p>
                            )}
                        </div>
                        <ProductBadges product={product} />
                    </div>

                    {!compact && (
                        <p className="mt-3 text-sm text-ink-muted leading-relaxed">
                            {description}
                            {longDescription && (
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setExpanded((v) => !v);
                                    }}
                                    className="ml-1 text-saffron-700 font-semibold hover:underline"
                                    data-testid={`read-more-${product.slug}`}
                                >
                                    {expanded ? "Show less" : "Read more"}
                                </button>
                            )}
                        </p>
                    )}

                    {!compact && (
                        <a
                            href={product.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-800 hover:underline break-all font-medium"
                            data-testid={`product-url-${product.slug}`}
                        >
                            🌐 {product.website_url}
                        </a>
                    )}

                    <div className="mt-4 flex flex-wrap items-center gap-2">
                        <button
                            onClick={handleVote}
                            disabled={hasVoted}
                            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-semibold transition-colors ${
                                hasVoted
                                    ? "border-indiagreen-200 bg-indiagreen-50 text-indiagreen-700 cursor-default"
                                    : "border-line bg-white text-ink hover:border-saffron/40 hover:text-saffron"
                            }`}
                            data-testid={`upvote-btn-${product.slug}`}
                            aria-label={`Upvote ${product.name}`}
                        >
                            <ChevronUp className="h-4 w-4" />
                            <span>{count}</span>
                        </button>

                        <a
                            href={product.website_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 rounded-lg bg-indiagreen text-white px-3 py-1.5 text-sm font-semibold hover:bg-indiagreen-700 transition-colors"
                            data-testid={`visit-btn-${product.slug}`}
                        >
                            Visit Website
                            <ArrowUpRight className="h-4 w-4" />
                        </a>

                        <Link
                            to={`/product/${product.slug}`}
                            className="ml-auto text-sm text-ink-muted hover:text-saffron font-medium"
                        >
                            Details →
                        </Link>
                    </div>
                </div>
            </div>
        </article>
    );
}
